import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import EmployerOnboardingForm from "@/components/forms/EmployerOnboardingForm";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, Users, Briefcase, LineChart, Clock, Building, ShieldCheck } from "lucide-react";

export default function HireFromUsPage() {
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="bg-white py-16 px-4 sm:px-6 lg:px-8 border-b">
          <div className="max-w-7xl mx-auto">
            <div className="md:flex md:items-center md:justify-between">
              <div className="md:max-w-2xl">
                <h1 className="text-4xl font-extrabold sm:text-5xl md:text-6xl text-primary-700">Hire From StaffAbility</h1>
                <p className="mt-4 text-xl text-slate-600">
                  Partner with us to find motivated, qualified employees while making a positive impact in your community.
                </p>
              </div>
              <div className="mt-8 md:mt-0 flex space-x-4">
                <Badge variant="outline" className="px-3 py-1 text-sm bg-gradient-to-r from-green-50 to-green-100 border-green-200">
                  <CheckCircle2 className="h-4 w-4 mr-1 text-green-600" />
                  <span className="text-slate-700">75+ Employer Partners</span>
                </Badge>
                <Badge variant="outline" className="px-3 py-1 text-sm bg-gradient-to-r from-blue-50 to-blue-100 border-blue-200">
                  <CheckCircle2 className="h-4 w-4 mr-1 text-blue-600" />
                  <span className="text-slate-700">83% Retention Rate</span>
                </Badge>
              </div>
            </div>
          </div>
        </section>

        {/* Business Benefits Section */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-slate-50 to-slate-100">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <span className="inline-block px-3 py-1 text-sm font-medium text-primary-700 bg-primary-100 rounded-full mb-4">Business Advantage</span>
              <h2 className="text-3xl font-bold text-slate-900">The StaffAbility Advantage for Employers</h2>
              <p className="mt-4 text-xl text-slate-600 max-w-3xl mx-auto">
                Hiring justice-involved individuals isn't just about social impact — it's a smart business decision with measurable benefits. <Link to="/research" className="text-primary-600 hover:text-primary-800 font-medium">Read our business case →</Link>
              </p>
            </div>

            <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 mb-12">
              <div className="bg-white p-8 rounded-xl shadow-md border border-slate-100 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 -mt-8 -mr-8 bg-gradient-to-br from-amber-100 to-amber-50 rounded-full opacity-70"></div>
                <div className="relative">
                  <div className="flex items-center mb-6">
                    <div className="flex-shrink-0 flex items-center justify-center h-14 w-14 rounded-full bg-gradient-to-r from-amber-400 to-amber-600 text-white shadow-md">
                      <Users className="h-7 w-7" />
                    </div>
                    <h3 className="ml-4 text-2xl font-semibold text-slate-900">Higher Retention & Loyalty</h3>
                  </div>
                  <div className="bg-amber-50 p-4 rounded-lg mb-4">
                    <p className="text-amber-800 font-semibold text-lg">83% retention rate after 12 months</p>
                    <p className="text-amber-800 text-sm">vs. 59% industry average</p>
                  </div>
                  <p className="text-slate-600 mb-4">
                    Justice-involved individuals often show higher loyalty to employers who give them a chance. Our candidates stay with their employers <strong>2.3x longer</strong> than traditional hires. With reduced turnover, you save significantly on hiring and training costs.
                  </p>
                  <ul className="space-y-2 text-slate-600">
                    <li className="flex items-start">
                      <CheckCircle2 className="h-5 w-5 text-amber-500 mr-2 flex-shrink-0 mt-0.5" />
                      <span>Average retention of 14 months vs. 6 months for traditional hires</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="h-5 w-5 text-amber-500 mr-2 flex-shrink-0 mt-0.5" />
                      <span>98% of our employers report above-average workplace loyalty</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="h-5 w-5 text-amber-500 mr-2 flex-shrink-0 mt-0.5" />
                      <span>Average $5,200 savings per hire in recruitment and training costs</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="bg-white p-8 rounded-xl shadow-md border border-slate-100 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 -mt-8 -mr-8 bg-gradient-to-br from-blue-100 to-blue-50 rounded-full opacity-70"></div>
                <div className="relative">
                  <div className="flex items-center mb-6">
                    <div className="flex-shrink-0 flex items-center justify-center h-14 w-14 rounded-full bg-gradient-to-r from-blue-400 to-blue-600 text-white shadow-md">
                      <Briefcase className="h-7 w-7" />
                    </div>
                    <h3 className="ml-4 text-2xl font-semibold text-slate-900">Exceptional Work Ethic</h3>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg mb-4">
                    <p className="text-blue-800 font-semibold text-lg">87% of employers report above-average performance</p>
                    <p className="text-blue-800 text-sm">from their justice-involved employees</p>
                  </div>
                  <p className="text-slate-600 mb-4">
                    Our candidates consistently demonstrate exceptional dedication and motivation. With something to prove and a deep appreciation for the opportunity, they often outperform traditional hires in punctuality, attendance, and productivity.
                  </p>
                  <ul className="space-y-2 text-slate-600">
                    <li className="flex items-start">
                      <CheckCircle2 className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0 mt-0.5" />
                      <span>91% attendance rate compared to 83% industry average</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0 mt-0.5" />
                      <span>72% reduced likelihood of tardiness or unexcused absences</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0 mt-0.5" />
                      <span>85% of employers report increased workplace morale</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
              <div className="bg-white p-6 rounded-lg shadow-md border border-slate-100">
                <div className="flex items-center mb-4">
                  <div className="flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-md bg-gradient-to-r from-green-400 to-emerald-600 text-white shadow-md">
                    <LineChart className="h-6 w-6" />
                  </div>
                  <h3 className="ml-4 text-xl font-medium text-slate-900">Tax Incentives</h3>
                </div>
                <p className="text-slate-600">
                  Access the Work Opportunity Tax Credit (WOTC) of up to $9,600 per eligible employee and other financial incentives. Our team handles all the paperwork for you. <Link to="/research" className="text-primary-600 hover:text-primary-800 font-medium">View business case →</Link>
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md border border-slate-100">
                <div className="flex items-center mb-4">
                  <div className="flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-md bg-gradient-to-r from-red-400 to-rose-600 text-white shadow-md">
                    <Clock className="h-6 w-6" />
                  </div>
                  <h3 className="ml-4 text-xl font-medium text-slate-900">Ongoing Support</h3>
                </div>
                <p className="text-slate-600">
                  Our case managers provide 24-month support to both employers and employees, addressing challenges before they impact work performance. <Link to="/research" className="text-primary-600 hover:text-primary-800 font-medium">Read implementation details →</Link>
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md border border-slate-100">
                <div className="flex items-center mb-4">
                  <div className="flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-md bg-gradient-to-r from-purple-400 to-purple-600 text-white shadow-md">
                    <Building className="h-6 w-6" />
                  </div>
                  <h3 className="ml-4 text-xl font-medium text-slate-900">Cultural Impact</h3>
                </div>
                <p className="text-slate-600">
                  78% of businesses report improved overall company culture and stronger team bonds after hiring through second-chance employment programs. <Link to="/research" className="text-primary-600 hover:text-primary-800 font-medium">Read white paper →</Link>
                </p>
              </div>
            </div>
          </div>
        </section>
        
        {/* Why Hire From Us */}
        <section className="py-12 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-slate-900">Our Comprehensive Service</h2>
              <p className="mt-4 text-xl text-slate-600 max-w-3xl mx-auto">
                We provide end-to-end support to ensure successful employment relationships.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
              <div className="bg-white p-6 rounded-lg shadow-md border border-slate-100">
                <div className="flex items-center mb-4">
                  <div className="flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-md bg-gradient-to-r from-amber-400 to-amber-600 text-white shadow-md">
                    <Users className="h-6 w-6" />
                  </div>
                  <h3 className="ml-4 text-xl font-medium text-slate-900">Pre-Screened Candidates</h3>
                </div>
                <p className="text-slate-600">
                  We carefully evaluate each candidate's skills, reliability, and work ethic to ensure they're job-ready.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md border border-slate-100">
                <div className="flex items-center mb-4">
                  <div className="flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-md bg-gradient-to-r from-blue-400 to-indigo-600 text-white shadow-md">
                    <Briefcase className="h-6 w-6" />
                  </div>
                  <h3 className="ml-4 text-xl font-medium text-slate-900">Job-Specific Training</h3>
                </div>
                <p className="text-slate-600">
                  Candidates receive training tailored to your industry and specific job requirements before placement.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md border border-slate-100">
                <div className="flex items-center mb-4">
                  <div className="flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-md bg-gradient-to-r from-pink-400 to-fuchsia-600 text-white shadow-md">
                    <ShieldCheck className="h-6 w-6" />
                  </div>
                  <h3 className="ml-4 text-xl font-medium text-slate-900">Risk Mitigation</h3>
                </div>
                <p className="text-slate-600">
                  Our case managers help address potential issues before they impact work performance, reducing turnover.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Employer Testimonials */}
        <section className="py-12 px-4 sm:px-6 lg:px-8 bg-slate-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-slate-900">Employer Testimonials</h2>
              <p className="mt-4 text-xl text-slate-600 max-w-3xl mx-auto">
                Hear from our employer partners about their experience hiring through StaffAbility.
              </p>
            </div>

            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="flex items-center mb-6">
                  <img 
                    src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-1.2.1&auto=format&fit=crop&w=120&q=80"
                    alt="Sarah Chen"
                    className="h-12 w-12 rounded-full object-cover"
                  />
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold text-slate-900">Sarah Chen</h3>
                    <p className="text-sm text-slate-500">HR Director, Metro Manufacturing</p>
                  </div>
                </div>
                <p className="text-slate-600 italic">
                  "StaffAbility has been an incredible partner in helping us fill our production roles. The candidates are well-prepared, motivated, and receive ongoing support that helps them succeed. Our retention rate with StaffAbility hires is significantly higher than our traditional hiring channels."
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="flex items-center mb-6">
                  <img 
                    src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&auto=format&fit=crop&w=120&q=80"
                    alt="Marcus Johnson"
                    className="h-12 w-12 rounded-full object-cover"
                  />
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold text-slate-900">Marcus Johnson</h3>
                    <p className="text-sm text-slate-500">Owner, Johnson's Logistics</p>
                  </div>
                </div>
                <p className="text-slate-600 italic">
                  "I was initially hesitant about hiring individuals with barriers to employment, but StaffAbility's thorough preparation and ongoing support made the process seamless. Our StaffAbility hires are among our most loyal and hardworking team members."
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="flex items-center mb-6">
                  <img 
                    src="https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?ixlib=rb-1.2.1&auto=format&fit=crop&w=120&q=80"
                    alt="Alicia Rivera"
                    className="h-12 w-12 rounded-full object-cover"
                  />
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold text-slate-900">Alicia Rivera</h3>
                    <p className="text-sm text-slate-500">Operations Manager, Riverside Health</p>
                  </div>
                </div>
                <p className="text-slate-600 italic">
                  "The StaffAbility team truly understands our needs as an employer. They don't just send candidates; they make thoughtful matches based on skills, workplace culture, and long-term potential. This approach has saved us time and resources in our hiring process."
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-12 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-slate-900">How It Works</h2>
              <p className="mt-4 text-xl text-slate-600 max-w-3xl mx-auto">
                Our streamlined process makes it easy to find qualified, motivated employees.
              </p>
            </div>

            <div className="max-w-3xl mx-auto">
              <div className="relative">
                <div className="absolute inset-0 flex items-center" aria-hidden="true">
                  <div className="w-full border-t border-slate-300"></div>
                </div>
                <div className="relative flex justify-start">
                  <span className="bg-white px-3 text-lg font-medium text-primary-600">Step 1</span>
                </div>
              </div>
              <div className="mt-4 mb-8 bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-semibold text-slate-900 mb-2">Partner Registration</h3>
                <p className="text-slate-600">
                  Complete our employer onboarding form to tell us about your company, the types of positions you hire for, and your workplace culture. This helps us understand your needs and make appropriate matches.
                </p>
              </div>

              <div className="relative">
                <div className="absolute inset-0 flex items-center" aria-hidden="true">
                  <div className="w-full border-t border-slate-300"></div>
                </div>
                <div className="relative flex justify-start">
                  <span className="bg-white px-3 text-lg font-medium text-primary-600">Step 2</span>
                </div>
              </div>
              <div className="mt-4 mb-8 bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-semibold text-slate-900 mb-2">Job Posting & Requirements</h3>
                <p className="text-slate-600">
                  Submit your job openings with detailed requirements. Our team will work with you to understand the specific skills, experience, and qualities needed for success in these roles.
                </p>
              </div>

              <div className="relative">
                <div className="absolute inset-0 flex items-center" aria-hidden="true">
                  <div className="w-full border-t border-slate-300"></div>
                </div>
                <div className="relative flex justify-start">
                  <span className="bg-white px-3 text-lg font-medium text-primary-600">Step 3</span>
                </div>
              </div>
              <div className="mt-4 mb-8 bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-semibold text-slate-900 mb-2">Candidate Matching</h3>
                <p className="text-slate-600">
                  We'll match your openings with pre-screened, qualified candidates who have the skills and attributes you need. You'll receive candidate profiles with relevant background information and why we believe they're a good fit.
                </p>
              </div>

              <div className="relative">
                <div className="absolute inset-0 flex items-center" aria-hidden="true">
                  <div className="w-full border-t border-slate-300"></div>
                </div>
                <div className="relative flex justify-start">
                  <span className="bg-white px-3 text-lg font-medium text-primary-600">Step 4</span>
                </div>
              </div>
              <div className="mt-4 mb-8 bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-semibold text-slate-900 mb-2">Ongoing Support & Success</h3>
                <p className="text-slate-600">
                  After hiring, our case managers provide ongoing support to ensure both you and your new employee succeed. We help navigate any challenges and provide resources to support retention and advancement.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-12 px-4 sm:px-6 lg:px-8 bg-slate-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-slate-900">Frequently Asked Questions</h2>
              <p className="mt-4 text-xl text-slate-600 max-w-3xl mx-auto">
                Common questions about hiring through StaffAbility.
              </p>
            </div>

            <div className="max-w-3xl mx-auto">
              <Accordion type="single" collapsible className="bg-white rounded-lg shadow-md overflow-hidden">
                <AccordionItem value="faq-1">
                  <AccordionTrigger className="px-6 py-4 hover:bg-slate-50">
                    Is there a cost to employers?
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-4">
                    <p className="text-slate-600">
                      No, our services are provided at no cost to employers. We're funded through grants, donations, and public workforce development programs.
                    </p>
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="faq-2">
                  <AccordionTrigger className="px-6 py-4 hover:bg-slate-50">
                    What types of jobs do you fill?
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-4">
                    <p className="text-slate-600">
                      We work with employers across various industries including manufacturing, logistics, healthcare support, hospitality, retail, and administrative roles. We're able to source candidates for entry-level to mid-level positions.
                    </p>
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="faq-3">
                  <AccordionTrigger className="px-6 py-4 hover:bg-slate-50">
                    What screening do candidates undergo?
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-4">
                    <p className="text-slate-600">
                      All candidates complete a thorough assessment including skills testing, background checks appropriate to the role, drug screening when required, and multiple interviews to evaluate work readiness, reliability, and fit.
                    </p>
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="faq-4">
                  <AccordionTrigger className="px-6 py-4 hover:bg-slate-50">
                    What support do you provide after hiring?
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-4">
                    <p className="text-slate-600">
                      We provide case management services for employees, regular check-ins with employers, mediation when needed, additional training opportunities, and connections to resources that help maintain stable employment.
                    </p>
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="faq-5">
                  <AccordionTrigger className="px-6 py-4 hover:bg-slate-50">
                    Do you offer tax incentive guidance?
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-4">
                    <p className="text-slate-600">
                      Yes, we help employers navigate available tax incentives, including the Work Opportunity Tax Credit (WOTC) and any local incentives that may apply when hiring from our candidate pool.
                    </p>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </div>
        </section>

        {/* Become a Partner */}
        <section className="py-12 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-slate-900">Become an Employer Partner</h2>
              <p className="mt-4 text-xl text-slate-600 max-w-3xl mx-auto">
                Complete the form below to start hiring qualified, motivated candidates through StaffAbility.
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-md max-w-2xl mx-auto">
              <h3 className="text-xl font-bold mb-4 text-slate-900 text-center">Employer Onboarding Form</h3>
              <p className="mb-6 text-slate-600 text-center">
                Please complete the form below to start the partnership process.
              </p>
              <EmployerOnboardingForm />
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}